import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { useEffectiveIsLocked } from '@/components/Recipe/FlowContext';
import { useIsNodeInRunningBatch } from '@/components/Recipe/RunFlow/batches.store';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import type { MenuAction } from '@/components/Menu/Actions';
import type { FileKind, NodeId } from 'web';
import type { UploadedAsset } from '@/types/api/assets';

interface DeleteAssetsSubMenuView {
  deleteAll: () => void | Promise<void>;
  deleteAllOthers: () => void | Promise<void>;
  deleteCurrent: () => void | Promise<void>;
  assets: FileKind[] | UploadedAsset[];
}

interface Texts {
  deleteCurrentAction?: string;
  deleteAllOthersAction?: string;
  deleteAllAction?: string;
  confirmationMessage?: string;
}

interface UseDeleteAssetsSubMenuProps {
  nodeId: NodeId;
  view: DeleteAssetsSubMenuView;
  texts?: Texts;
  allowDeletingLastFile?: boolean;
}

export const useDeleteAssetsSubMenu = ({
  nodeId,
  view,
  texts,
  allowDeletingLastFile = true,
}: UseDeleteAssetsSubMenuProps): MenuAction[] => {
  const { t } = useTranslation();
  const isLocked = useEffectiveIsLocked(nodeId);
  const isNodeInRunningBatch = useIsNodeInRunningBatch(nodeId);
  const assets = view.assets;
  const { openConfirmationDialog } = useConfirmationDialogStore();

  const textsData = useMemo(() => {
    const defaultTexts = {
      deleteCurrentAction: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.DELETE_CURRENT_UPLOAD),
      deleteAllOthersAction: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.DELETE_ALL_OTHERS_UPLOADS),
      deleteAllAction: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.DELETE_ALL_UPLOADS),
      confirmationMessage: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU_CONFIRMATION_DIALOG.DELETE_ALL_UPLOADS_MESSAGE),
    };
    return { ...defaultTexts, ...texts };
  }, [texts, t]);

  const isDeleteDisabled = useMemo(() => {
    return !assets?.length || isLocked || isNodeInRunningBatch || (!allowDeletingLastFile && assets?.length === 1);
  }, [assets, isLocked, isNodeInRunningBatch, allowDeletingLastFile]);

  const onDeleteAll = useCallback(async () => {
    await view.deleteAll();
  }, [view]);

  const deleteAllAction: MenuAction[] = useMemo(() => {
    if (!allowDeletingLastFile) {
      return [];
    }

    return [
      {
        type: 'divider',
      },
      {
        name: textsData.deleteAllAction,
        action: () =>
          openConfirmationDialog({
            title: textsData.confirmationMessage,
            onConfirm: onDeleteAll,
          }),
        disabled: isDeleteDisabled,
      },
    ];
  }, [textsData, isDeleteDisabled, openConfirmationDialog, allowDeletingLastFile, onDeleteAll]);

  return useMemo(() => {
    return [
      {
        type: 'divider',
      },
      {
        name: textsData.deleteCurrentAction,
        action: () => view.deleteCurrent(),
        disabled: isDeleteDisabled,
      },
      {
        name: textsData.deleteAllOthersAction,
        action: () => view.deleteAllOthers(),
        disabled: isDeleteDisabled || assets.length === 1,
      },
      ...deleteAllAction,
    ];
  }, [textsData, view, isDeleteDisabled, assets.length, deleteAllAction]);
};
